// vehicle_trips PROJEKCIA — UI číta odtiaľto; autoritatívny dôkaz je vehicle_trip_ledger.
// Spec: MasterProject/feature-ideas/ideas/todue-kniha-jazd-SPEC-v2.md §2–§6.
//
// INVARIANT (dôkazná sila): projekcia je VŽDY odvoditeľná IBA z ledgeru. Jedna čistá
// fold funkcia (foldTripEvents) buduje projekciu z eventov; zápis (recordTripEvent) aj
// obnova (rebuildProjection) ju zdieľajú → nemôžu sa rozísť. Ak sa projekcia niekedy
// poškodí (crash, čiastočný zápis, restore), rebuildProjection ju deterministicky
// zregeneruje z nemenného ledgeru.
//
// ATOMICITA (handoff bod 2): recordTripEvent zapisuje ledger event AJ projekčné riadky
// v JEDNEJ db.transaction('rw', …) → commitnú spolu alebo vôbec. appendTripEvent dostáva
// tx handle (conn) — beží vnútri tej istej transakcie.
//
// DETERMINIZMUS pre sync: projekčný riadok NEMÁ náhodné/časové polia z Date.now(). rev =
// počet ledger eventov ktoré trip ovplyvnili, updated_at = created_at posledného z nich.
// Dva klienty ktoré prehrajú ten istý ledger vyrobia BIT-IDENTICKÚ projekciu → LWW sync
// konverguje bez zbytočných rev bumpov (mení sa len reálne zmenený riadok).
import { db } from './db';
import {
  appendTripEvent, listTripLedger, tripLedgerEventId, TRIP_LEDGER_EVENT_TYPES,
} from './vehicleLedger';

// ─── Trip lifecycle eventy (podmnožina ledger enumu, ktorá projektuje do vehicle_trips) ─
// odometer_recorded / cost_recorded / period_closed / … majú vlastné projekcie (neskôr) —
// nepatria sem a recordTripEvent ich odmietne.
const TRIP_PROJECTION_EVENTS = new Set([
  'trip_proposed', 'trip_confirmed', 'trip_amended', 'trip_voided', 'trip_corrected',
]);

// Vecné polia jazdy prenášané z payloadu do projekcie (whitelist — žiadny event
// nesmie do projekcie prepašovať cudzí stĺpec).
const TRIP_VALUE_FIELDS = [
  'driver_id', 'driver_name', 'trip_type',
  'occurred_from', 'occurred_to', 'tz_offset',
  'purpose', 'route_from', 'route_to',
  'km', 'odo_before', 'odo_after', 'stops',
];

function pickTripValues(src) {
  const out = {};
  if (!src || typeof src !== 'object') return out;
  for (const k of TRIP_VALUE_FIELDS) {
    if (src[k] !== undefined) out[k] = src[k];
  }
  return out;
}

// Prázdny projekčný skelet — všetky stĺpce vehicle_trips definované (žiadne undefined
// do DB, deterministický tvar pre hash/rebuild porovnanie).
function emptyTripRow(id, vehicleId) {
  return {
    id,
    vehicle_id: vehicleId,
    seq_no: null,
    driver_id: null, driver_name: null, confirmed_by: null,
    trip_type: null,
    occurred_from: null, occurred_to: null, tz_offset: null,
    purpose: null, route_from: null, route_to: null,
    km: null, odo_before: null, odo_after: null,
    status: 'proposed', corrects_id: null, void_reason: null,
    period_key: null, closed_at: null,
    stops: null, provenance: null, custom: null,
    // LWW/kernel — deterministicky doplnené vo foldTripEvents (nie Date.now()).
    created_at: null, updated_at: null, rev: 0,
    deleted_at: null, schema_version: 1, obligation_pack_version: 0,
  };
}

/**
 * foldTripEvents(events) — ČISTÁ deriv funkcia: ledger eventy JEDNÉHO vozidla
 * (v seq poradí) → Map<trip_id, projekčný riadok vehicle_trips>.
 *
 * Poradové číslo jazdy (seq_no): neprerušená rada per vozidlo, prideľuje sa keď
 * jazda prvýkrát „vznikne" ako reálna (trip_confirmed alebo trip_corrected). Raz
 * pridelené sa NEMENÍ — stornovaná jazda si číslo ponechá (viditeľná medzera =
 * dôkazná sila, nie chyba), oprava dostane ďalšie číslo v rade.
 */
export function foldTripEvents(events) {
  // Fold je PER-VOZIDLO (jeden nextSeqNo counter). Zmiešané vehicle_id by vyrobilo
  // krížovú poradovú radu, ktorá nesedí so žiadnym autoritatívnym ledgerom (Codex #5).
  const vehicleIds = new Set(events.map((e) => e.vehicle_id));
  if (vehicleIds.size > 1) {
    throw new Error(`[v2/vehicleTrips] foldTripEvents dostal eventy ${vehicleIds.size} vozidiel — fold je per-vozidlo (volaj s eventmi jedného vozidla)`);
  }
  const sorted = [...events].sort((a, b) => a.seq - b.seq);
  const rows = new Map();
  let nextSeqNo = 0; // najvyššie pridelené poradové číslo jazdy pre toto vozidlo

  for (const ev of sorted) {
    if (!TRIP_PROJECTION_EVENTS.has(ev.event_type)) continue;
    const payload = ev.payload || {};

    if (ev.event_type === 'trip_proposed') {
      // Návrh smie VZNIKNÚŤ len raz. Proposed na už existujúci trip (potvrdený/
      // stornovaný) by prepísal skelet a vymazal pridelené seq_no + stav (Codex #4).
      if (rows.has(ev.trip_id)) continue;
      const row = emptyTripRow(ev.trip_id, ev.vehicle_id);
      Object.assign(row, pickTripValues(payload.draft || payload));
      row.provenance = payload.provenance ?? null;
      row.status = 'proposed';
      stampRow(row, ev);
      rows.set(ev.trip_id, row);
      continue;
    }

    if (ev.event_type === 'trip_confirmed') {
      const row = rows.get(ev.trip_id) || emptyTripRow(ev.trip_id, ev.vehicle_id);
      Object.assign(row, pickTripValues(payload.confirmed || payload));
      if (payload.confirmed_by !== undefined) row.confirmed_by = payload.confirmed_by;
      if (payload.provenance !== undefined) row.provenance = payload.provenance;
      row.status = 'active';
      if (row.seq_no == null) { nextSeqNo += 1; row.seq_no = nextSeqNo; }
      stampRow(row, ev);
      rows.set(ev.trip_id, row);
      continue;
    }

    if (ev.event_type === 'trip_amended') {
      const row = rows.get(ev.trip_id);
      if (!row) continue; // amend bez predchodcu — ignoruj (ledger je zdroj, projekcia nefabrikuje)
      if (row.status === 'voided' || row.closed_at) continue; // po storne/uzávierke sa needituje (§6)
      Object.assign(row, pickTripValues(payload.changes || payload));
      if (payload.confirmed_by !== undefined) row.confirmed_by = payload.confirmed_by;
      stampRow(row, ev);
      continue;
    }

    if (ev.event_type === 'trip_voided') {
      const row = rows.get(ev.trip_id);
      if (!row) continue;
      row.status = 'voided';
      row.void_reason = payload.reason ?? null;
      stampRow(row, ev);
      continue;
    }

    if (ev.event_type === 'trip_corrected') {
      // Nový (opravný) záznam — trip_id MUSÍ byť NOVÝ trip, payload.corrects_id je starý.
      // Ak by trip_id kolidoval s existujúcim (napr. corrects_id === trip_id), prepis by
      // reaktivoval stornovaný riadok a spravil ho sebe-opravujúcim (Codex #4). Neprepisuj.
      if (rows.has(ev.trip_id)) continue;
      const row = emptyTripRow(ev.trip_id, ev.vehicle_id);
      Object.assign(row, pickTripValues(payload.confirmed || payload));
      if (payload.confirmed_by !== undefined) row.confirmed_by = payload.confirmed_by;
      row.corrects_id = payload.corrects_id ?? null;
      row.status = 'active';
      if (row.seq_no == null) { nextSeqNo += 1; row.seq_no = nextSeqNo; }
      stampRow(row, ev);
      rows.set(ev.trip_id, row);
      continue;
    }
  }
  return rows;
}

// Deterministický LWW razítko z eventu: rev = poradie aplikovaného eventu na tento
// riadok, created_at = prvý event, updated_at = tento event. Žiadny Date.now().
function stampRow(row, ev) {
  row.rev = (row.rev || 0) + 1;
  if (!row.created_at) row.created_at = ev.created_at;
  row.updated_at = ev.created_at;
}

// Porovná projekčný riadok s uloženým — či treba zápis (šetrí rev bump a sync).
function rowDiffers(next, prev) {
  if (!prev) return true;
  for (const k of Object.keys(next)) {
    if (JSON.stringify(next[k] ?? null) !== JSON.stringify(prev[k] ?? null)) return true;
  }
  return false;
}

/**
 * recordTripEvent — JEDINÁ zápisová cesta pre trip lifecycle. Ledger event + projekcia
 * vehicle_trips ATOMICKY v jednej transakcii. Vracia { event, projection } kde projection
 * je aktuálny riadok dotknutého tripu (alebo null pri storne pôvodného).
 *
 * @param {string}  eventKey    deterministický kľúč operácie (idempotentný retry = no-op)
 * @param {string}  vehicleId
 * @param {string}  eventType   trip_proposed|trip_confirmed|trip_amended|trip_voided|trip_corrected
 * @param {string}  tripId      id jazdy v projekcii (pri trip_corrected = NOVÝ trip)
 * @param {object}  payload
 * @param {string|object} actor
 * @param {string} [occurredAt] deklarovaný čas jazdy (ISO)
 */
export async function recordTripEvent({
  eventKey, vehicleId, eventType, tripId, payload, actor, occurredAt,
} = {}) {
  if (!TRIP_PROJECTION_EVENTS.has(eventType)) {
    const known = TRIP_LEDGER_EVENT_TYPES.includes(eventType);
    throw new Error(
      `[v2/vehicleTrips] recordTripEvent nepodporuje event_type ${JSON.stringify(eventType)}`
      + (known ? ' (nie je trip lifecycle event — má vlastnú projekciu)' : ' (neznámy event)'),
    );
  }
  if (!tripId || typeof tripId !== 'string') {
    throw new Error(`[v2/vehicleTrips] tripId je povinný, dostal ${JSON.stringify(tripId)}`);
  }
  if (eventType === 'trip_corrected') {
    const correctsId = payload && typeof payload === 'object' ? payload.corrects_id : null;
    if (!correctsId || typeof correctsId !== 'string') {
      throw new Error('[v2/vehicleTrips] trip_corrected vyžaduje payload.corrects_id (id stornovaného záznamu)');
    }
    if (correctsId === tripId) {
      throw new Error('[v2/vehicleTrips] trip_corrected: opravný záznam nemôže mať to isté id ako opravovaný (musí byť nový trip)');
    }
  }

  return db.transaction('rw', db.vehicle_trip_ledger, db.vehicle_trips, async (tx) => {
    const existingTrip = await tx.vehicle_trips.get(tripId);
    // #5 (analógia vehicleCosts): tripId je globálny PK projekcie — kolízia naprieč
    // vozidlami by ticho prepísala cudzí záznam. Idempotency-safe (retry má rovnaké vehicleId).
    if (existingTrip && existingTrip.vehicle_id !== vehicleId) {
      throw new Error(`[v2/vehicleTrips] tripId ${tripId} už patrí inému vozidlu — id musí byť globálne unikátne`);
    }
    // #3: proposed/corrected musí niesť NOVÉ id (nie retry inej operácie s kolidujúcim id).
    if ((eventType === 'trip_proposed' || eventType === 'trip_corrected') && existingTrip) {
      const led = await listTripLedger(vehicleId, tx);
      const owner = led.find((e) => e.trip_id === tripId && (e.event_type === 'trip_proposed' || e.event_type === 'trip_corrected'));
      const eventId = await tripLedgerEventId(eventKey);
      if (owner && owner.id !== eventId) {
        throw new Error(`[v2/vehicleTrips] tripId ${tripId} už existuje — ${eventType} vyžaduje nové id`);
      }
    }
    // #4: oprava musí referencovať EXISTUJÚCU stornovanú jazdu rovnakého vozidla.
    if (eventType === 'trip_corrected') {
      const target = await tx.vehicle_trips.get(payload?.corrects_id);
      if (!target || target.vehicle_id !== vehicleId || target.status !== 'voided') {
        throw new Error(`[v2/vehicleTrips] trip_corrected.corrects_id musí byť existujúca STORNOVANÁ jazda rovnakého vozidla (dostal ${JSON.stringify(payload?.corrects_id)})`);
      }
    }

    // 1) Autoritatívny zápis do ledgeru (vnútri tejto tx cez conn).
    const event = await appendTripEvent({
      eventKey, vehicleId, eventType, tripId, payload, actor, occurredAt, conn: tx,
    });

    // 2) Prehraj ledger vozidla → nová projekcia (fold). Číta AKTUÁLNY stav v tx
    //    (vrátane práve zapísaného eventu) → projekcia je vždy v zhode s ledgerom.
    const projection = await syncProjectionFromLedger(vehicleId, tx);
    return { event, projection: projection.get(tripId) ?? null };
  });
}

// Prehrá ledger vozidla, upsertne LEN zmenené riadky projekcie (rev bump len na reálne
// dotknutých). Vracia Map<trip_id,row>. `tx` = transakčný handle (povinný — volá sa z tx).
async function syncProjectionFromLedger(vehicleId, tx) {
  const events = await listTripLedger(vehicleId, tx);
  const folded = foldTripEvents(events);
  for (const [id, row] of folded) {
    const prev = await tx.vehicle_trips.get(id);
    if (rowDiffers(row, prev)) await tx.vehicle_trips.put(row);
  }
  return folded;
}

/**
 * rebuildProjection(vehicleId) — recovery: zahoď/prepíš projekciu vozidla a postav ju
 * nanovo z nemenného ledgeru. Idempotentné. Použi po restore zálohy, po detekcii
 * nekonzistencie (verifyTripProjection), alebo po zmene fold logiky (migrácia).
 *
 * Odstráni aj „sirotské" projekčné riadky, ktoré v ledgeri už nemajú oporu (soft-delete
 * cez deleted_at — LWW sync ich propaguje ako zmazané, nemažeme fyzicky).
 */
export async function rebuildProjection(vehicleId) {
  if (!vehicleId || typeof vehicleId !== 'string') {
    // Bez id by listTripLedger vrátil VŠETKY vozidlá → fold by zmiešal poradové rady (Codex #5).
    throw new Error(`[v2/vehicleTrips] rebuildProjection vyžaduje vehicleId, dostal ${JSON.stringify(vehicleId)}`);
  }
  return db.transaction('rw', db.vehicle_trip_ledger, db.vehicle_trips, async (tx) => {
    const events = await listTripLedger(vehicleId, tx);
    const folded = foldTripEvents(events);
    // Id-y s oporou v LOKÁLNOM ledgeri (aspoň jeden TRIP event). Filter podľa event-family — ledger
    // je ZDIEĽANÝ s nákladmi, bez filtra by cost id (v tom istom `trip_id` stĺpci) falošne označil
    // cudzí trip riadok za lokálnu sirotu (Codex cross-family verify). Riadok mimo = CUDZÍ (synced
    // z iného zariadenia), NIE lokálna sirota.
    const ledgerIds = new Set(
      events.filter((e) => TRIP_PROJECTION_EVENTS.has(e.event_type)).map((e) => e.trip_id).filter(Boolean),
    );

    const existing = (await tx.vehicle_trips.toArray()).filter((r) => r.vehicle_id === vehicleId);
    const seen = new Set();
    for (const [id, row] of folded) {
      const prev = await tx.vehicle_trips.get(id);
      seen.add(id);
      // Kolízia id s riadkom INÉHO vozidla → neklobrni (recordTripEvent garantuje globálne unikátne
      // id; kolízia = tamper/sync bug). Cudzí riadok radšej nechaj (Codex verify, money-path).
      if (prev && prev.vehicle_id !== vehicleId) continue;
      if (rowDiffers(row, prev)) await tx.vehicle_trips.put(row);
    }
    // Sirota v projekcii → soft-delete (nie fyzický; ledger zaceľuje diery — SPEC-v2 §3), ale LEN
    // lokálne-autorská (id má event v lokálnom ledgeri). CUDZIE cross-device riadky NEMAŽ — inak by
    // sa delete syncol späť a zmazal platné jazdy iného zariadenia (Daniel rozhodol PRESERVE, B,
    // 2026-07-27; verifyTripProjection ich nahlási v `foreign`, nedeštruktívne).
    for (const r of existing) {
      if (ledgerIds.has(r.id) && !seen.has(r.id) && !r.deleted_at) {
        await tx.vehicle_trips.put({ ...r, deleted_at: new Date().toISOString(), rev: (r.rev || 1) + 1 });
      }
    }
    return { rebuilt: folded.size };
  });
}

/**
 * verifyTripProjection(vehicleId) — over že uložená projekcia SEDÍ s foldom ledgeru.
 * Vracia { ok, mismatches: [{ trip_id, reason }], foreign: [{ trip_id, reason }] }. Read-only.
 * `foreign` = riadky mimo LOKÁLNEHO ledgeru (synced z iného zariadenia) — legitímne v multi-device,
 * NErátajú sa do `ok` (Daniel PRESERVE, B, 2026-07-27). `mismatches` = skutočné nezhody (fold vs
 * uložené, alebo lokálne-autorská sirota) — tie lámu `ok`.
 */
export async function verifyTripProjection(vehicleId) {
  if (!vehicleId || typeof vehicleId !== 'string') {
    throw new Error(`[v2/vehicleTrips] verifyTripProjection vyžaduje vehicleId, dostal ${JSON.stringify(vehicleId)}`);
  }
  const events = await listTripLedger(vehicleId);
  const folded = foldTripEvents(events);
  // Filter podľa TRIP family — ledger zdieľaný s nákladmi (Codex verify).
  const ledgerIds = new Set(
    events.filter((e) => TRIP_PROJECTION_EVENTS.has(e.event_type)).map((e) => e.trip_id).filter(Boolean),
  );
  const stored = (await db.vehicle_trips.toArray())
    .filter((r) => r.vehicle_id === vehicleId && !r.deleted_at);
  const mismatches = [];
  const foreign = [];

  for (const [id, row] of folded) {
    const prev = stored.find((r) => r.id === id);
    if (!prev) { mismatches.push({ trip_id: id, reason: 'chýba v projekcii' }); continue; }
    if (rowDiffers(row, prev)) mismatches.push({ trip_id: id, reason: 'projekcia nesedí s ledgerom' });
  }
  for (const r of stored) {
    if (folded.has(r.id)) continue;
    // Riadok mimo foldu: má oporu v lokálnom ledgeri → skutočná sirota (láme ok); inak cudzí
    // cross-device riadok → len nahlás, ok ostáva (legitímny v multi-device).
    if (ledgerIds.has(r.id)) mismatches.push({ trip_id: r.id, reason: 'projekcia navyše (má event v ledgeri, fold ju nedá)' });
    else foreign.push({ trip_id: r.id, reason: 'cudzí riadok — mimo lokálneho ledgeru (iné zariadenie)' });
  }
  return { ok: mismatches.length === 0, mismatches, foreign };
}

// listTrips(vehicleId?) — projekcia pre UI (bez soft-deleted), zotriedená podľa seq_no.
export async function listTrips(vehicleId) {
  const rows = (await db.vehicle_trips.toArray()).filter((r) => !r.deleted_at);
  const filtered = vehicleId ? rows.filter((r) => r.vehicle_id === vehicleId) : rows;
  return filtered.sort((a, b) => {
    if (a.vehicle_id !== b.vehicle_id) return a.vehicle_id < b.vehicle_id ? -1 : 1;
    return (a.seq_no ?? 1e9) - (b.seq_no ?? 1e9);
  });
}
